'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { createClient } from '../../lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';


interface PropertyForm {
  tipo: string;
  finalidade: string;
  titulo: string;
  descricao: string;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  estado: string;
  preco: string;
  condominio: string;
  iptu: string;
  area_total: string;
  area_util: string;
  quartos: string;
  suites: string;
  banheiros: string;
  vagas: string;
  andar: string;
  total_andares: string;
  status: string;
  diferenciais: string[];
  fotos: string[];
}

interface Property extends PropertyForm {
  id: string;
  created_at: string;
}

const TIPOS = ['Apartamento', 'Casa', 'Cobertura', 'Studio', 'Loft', 'Terreno', 'Sala Comercial', 'Galpão'];
const FINALIDADES = ['Venda', 'Locação', 'Venda e Locação'];
const STATUS_OPTIONS = ['Disponível', 'Em Negociação', 'Vendido', 'Alugado'];
const DIFERENCIAIS_OPTIONS = [
  'Piscina', 'Academia', 'Churrasqueira', 'Varanda Gourmet', 'Portaria 24h',
  'Elevador', 'Gerador', 'Playground', 'Salão de Festas', 'Coworking',
  'Pet Friendly', 'Energia Solar', 'Ar Condicionado', 'Mobiliado', 'Lareira',
];

const ESTADOS_BR = [
  'AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG',
  'PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO',
];

const initialForm: PropertyForm = {
  tipo: '', finalidade: '', titulo: '', descricao: '',
  cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', estado: 'PR',
  preco: '', condominio: '', iptu: '',
  area_total: '', area_util: '', quartos: '', suites: '', banheiros: '', vagas: '',
  andar: '', total_andares: '', status: 'Disponível',
  diferenciais: [], fotos: [],
};

function formatCurrency(value: string): string {
  const nums = value.replace(/\D/g, '');
  if (!nums) return '';
  const n = parseInt(nums, 10) / 100;
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function parseCurrency(value: string): string {
  return value.replace(/\D/g, '');
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR');
}

export default function ImoveisPage() {
  const [form, setForm] = useState<PropertyForm>(initialForm);
  const [submitted, setSubmitted] = useState(false);
  const [properties, setProperties] = useState<Property[]>([]);
  const [activeTab, setActiveTab] = useState<'cadastrar' | 'listagem'>('listagem');
  const [errors, setErrors] = useState<Partial<Record<keyof PropertyForm, string>>>({});
  const [photoPreview, setPhotoPreview] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const supabase = createClient();
  const { user, loading: authLoading, signOut } = useAuth();
  const router = useRouter();

  // Auth guard: redirect unauthenticated users trying to access "cadastrar" tab
  useEffect(() => {
    if (!authLoading && !user) {
      // If on cadastrar tab without auth, switch to listagem
      if (activeTab === 'cadastrar') {
        setActiveTab('listagem');
      }
    }
  }, [user, authLoading, activeTab]);

  const handleTabChange = (tab: 'cadastrar' | 'listagem') => {
    if (tab === 'cadastrar' && !user) {
      router.push('/imoveis/login');
      return;
    }
    setActiveTab(tab);
  };

  const handleSignOut = async () => {
    await signOut();
    setActiveTab('listagem');
  };

  const loadProperties = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const { data, error } = await supabase
        .from('imoveis')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Erro ao carregar imóveis:', error.message);
        setFetchError('Não foi possível carregar o portfólio.');
      } else {
        setProperties((data as Property[]) || []);
      }
    } catch (err: any) {
      console.error('Erro inesperado:', err.message);
      setFetchError('Erro inesperado ao carregar imóveis.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProperties();
  }, [loadProperties]);

  const set = (field: keyof PropertyForm, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const toggleDiferencial = (item: string) => {
    setForm(prev => ({
      ...prev,
      diferenciais: prev.diferenciais.includes(item)
        ? prev.diferenciais.filter(d => d !== item)
        : [...prev.diferenciais, item],
    }));
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const result = ev.target?.result as string;
        setPhotoPreview(prev => [...prev, result]);
        setForm(prev => ({ ...prev, fotos: [...prev.fotos, result] }));
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (idx: number) => {
    setPhotoPreview(prev => prev.filter((_, i) => i !== idx));
    setForm(prev => ({ ...prev, fotos: prev.fotos.filter((_, i) => i !== idx) }));
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof PropertyForm, string>> = {};
    if (!form.tipo) newErrors.tipo = 'Selecione o tipo do imóvel';
    if (!form.finalidade) newErrors.finalidade = 'Selecione a finalidade';
    if (!form.titulo.trim()) newErrors.titulo = 'Informe o título do anúncio';
    if (!form.logradouro.trim()) newErrors.logradouro = 'Informe o endereço';
    if (!form.cidade.trim()) newErrors.cidade = 'Informe a cidade';
    if (!form.preco) newErrors.preco = 'Informe o valor';
    if (!form.area_util) newErrors.area_util = 'Informe a área útil';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    try {
      const { data, error } = await supabase
        .from('imoveis')
        .insert({
          tipo: form.tipo,
          finalidade: form.finalidade,
          titulo: form.titulo,
          descricao: form.descricao,
          cep: form.cep,
          logradouro: form.logradouro,
          numero: form.numero,
          complemento: form.complemento,
          bairro: form.bairro,
          cidade: form.cidade,
          estado: form.estado,
          preco: form.preco,
          condominio: form.condominio,
          iptu: form.iptu,
          area_total: form.area_total,
          area_util: form.area_util,
          quartos: form.quartos,
          suites: form.suites,
          banheiros: form.banheiros,
          vagas: form.vagas,
          andar: form.andar,
          total_andares: form.total_andares,
          status: form.status,
          diferenciais: form.diferenciais,
          fotos: form.fotos,
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao cadastrar imóvel:', error.message);
        alert('Erro ao cadastrar imóvel. Tente novamente.');
        return;
      }

      // Send admin email notification (fire-and-forget)
      try {
        const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
        await fetch(`${supabaseUrl}/functions/v1/send-admin-email`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'new_property',
            data: {
              titulo: form.titulo,
              tipo: form.tipo,
              finalidade: form.finalidade,
              bairro: form.bairro,
              cidade: form.cidade,
              estado: form.estado,
              preco: form.preco,
              status: form.status,
            },
          }),
        });
      } catch {
        // Email notification failure should not block the user flow
      }

      setProperties(prev => [data as Property, ...prev]);
      setForm(initialForm);
      setPhotoPreview([]);
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 4000);
      setActiveTab('listagem');
    } catch (err: any) {
      console.error('Erro inesperado:', err.message);
      alert('Erro inesperado. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = (field?: keyof PropertyForm) =>
    `w-full bg-bg-card border ${field && errors[field] ? 'border-red-500/60' : 'border-border-light'} rounded-sm px-4 py-3 text-fg text-sm placeholder-fg-subtle focus:border-accent focus:ring-0 transition-colors duration-200`;

  const labelClass = 'block text-[11px] font-medium uppercase tracking-[0.14em] text-fg-muted mb-1.5';

  return (
    <main className="bg-bg text-fg min-h-screen overflow-x-hidden">
      <Header />

      {/* Page Header */}
      <section className="pt-32 pb-12 px-6 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 to-transparent pointer-events-none" />
        <div className="max-w-5xl mx-auto relative flex items-start justify-between gap-4">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.22em] text-accent mb-3">
              Gestão de Portfólio
            </p>
            <h1 className="font-display text-4xl md:text-5xl font-light italic text-fg mb-4">
              Cadastro de Imóveis
            </h1>
            <p className="text-fg-muted text-sm max-w-xl">
              Registre novos imóveis ao portfólio VS Cardoso com todas as informações necessárias para uma apresentação profissional.
            </p>
          </div>
          {user && (
            <button
              onClick={handleSignOut}
              className="flex-shrink-0 mt-2 px-5 py-2.5 border border-border-light text-fg-muted text-[11px] font-medium uppercase tracking-[0.14em] rounded-sm hover:border-accent/50 hover:text-fg transition-colors duration-200 flex items-center gap-2"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M5 2H2a1 1 0 00-1 1v8a1 1 0 001 1h3" strokeLinecap="round" />
                <path d="M9 10l3-3-3-3M12 7H5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Sair
            </button>
          )}
        </div>
      </section>

      {/* Tabs */}
      <div className="px-6 max-w-5xl mx-auto mb-8">
        <div className="flex gap-1 bg-bg-card border border-border-light rounded-sm p-1 w-fit">
          <button
            onClick={() => handleTabChange('listagem')}
            className={`px-6 py-2.5 text-[11px] font-medium uppercase tracking-[0.14em] rounded-sm transition-all duration-200 ${
              activeTab === 'listagem' ?'bg-accent text-bg' : 'text-fg-muted hover:text-fg'
            }`}
          >
            {`Portfólio${properties.length > 0 ? ` (${properties.length})` : ''}`}
          </button>
          {!authLoading && user && (
            <button
              onClick={() => handleTabChange('cadastrar')}
              className={`px-6 py-2.5 text-[11px] font-medium uppercase tracking-[0.14em] rounded-sm transition-all duration-200 ${
                activeTab === 'cadastrar' ?'bg-accent text-bg' : 'text-fg-muted hover:text-fg'
              }`}
            >
              Novo Imóvel
            </button>
          )}
        </div>
      </div>

      {/* Success Toast */}
      {submitted && (
        <div className="fixed top-24 right-6 z-50 glass-card px-6 py-4 rounded-sm flex items-center gap-3 shadow-2xl animate-fade-in">
          <div className="w-5 h-5 rounded-full bg-accent flex items-center justify-center flex-shrink-0">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M2 5l2 2 4-4" stroke="#0A0F0D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span className="text-sm text-fg">Imóvel cadastrado com sucesso!</span>
        </div>
      )}

      <div className="px-6 max-w-5xl mx-auto pb-20">

        {/* FORM TAB */}
        {activeTab === 'cadastrar' && (
          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-6">

              {/* Tipo & Finalidade */}
              <div className="bg-bg-card border border-border-light rounded-sm p-6">
                <h2 className="text-[11px] font-medium uppercase tracking-[0.18em] text-accent mb-5">
                  Classificação
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={labelClass}>Tipo de Imóvel *</label>
                    <select value={form.tipo} onChange={e => set('tipo', e.target.value)} className={inputClass('tipo')}>
                      <option value="">Selecione</option>
                      {TIPOS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                    {errors.tipo && <p className="text-red-400 text-xs mt-1">{errors.tipo}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Finalidade *</label>
                    <select value={form.finalidade} onChange={e => set('finalidade', e.target.value)} className={inputClass('finalidade')}>
                      <option value="">Selecione</option>
                      {FINALIDADES.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                    {errors.finalidade && <p className="text-red-400 text-xs mt-1">{errors.finalidade}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Status</label>
                    <select value={form.status} onChange={e => set('status', e.target.value)} className={inputClass()}>
                      {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Título & Descrição */}
              <div className="bg-bg-card border border-border-light rounded-sm p-6">
                <h2 className="text-[11px] font-medium uppercase tracking-[0.18em] text-accent mb-5">
                  Apresentação
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>Título do Anúncio *</label>
                    <input
                      type="text"
                      value={form.titulo}
                      onChange={e => set('titulo', e.target.value)}
                      placeholder="Ex: Apartamento alto padrão com vista panorâmica"
                      className={inputClass('titulo')}
                    />
                    {errors.titulo && <p className="text-red-400 text-xs mt-1">{errors.titulo}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Descrição</label>
                    <textarea
                      value={form.descricao}
                      onChange={e => set('descricao', e.target.value)}
                      rows={4}
                      placeholder="Descreva os diferenciais, localização, acabamentos e pontos de destaque do imóvel..."
                      className={`${inputClass()} resize-none`}
                    />
                  </div>
                </div>
              </div>

              {/* Endereço */}
              <div className="bg-bg-card border border-border-light rounded-sm p-6">
                <h2 className="text-[11px] font-medium uppercase tracking-[0.18em] text-accent mb-5">
                  Localização
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                  <div className="md:col-span-2">
                    <label className={labelClass}>CEP</label>
                    <input type="text" value={form.cep} onChange={e => set('cep', e.target.value)} placeholder="00000-000" maxLength={9} className={inputClass()} />
                  </div>
                  <div className="md:col-span-4">
                    <label className={labelClass}>Logradouro *</label>
                    <input type="text" value={form.logradouro} onChange={e => set('logradouro', e.target.value)} placeholder="Rua, Avenida, Alameda..." className={inputClass('logradouro')} />
                    {errors.logradouro && <p className="text-red-400 text-xs mt-1">{errors.logradouro}</p>}
                  </div>
                  <div className="md:col-span-1">
                    <label className={labelClass}>Número</label>
                    <input type="text" value={form.numero} onChange={e => set('numero', e.target.value)} placeholder="Nº" className={inputClass()} />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>Complemento</label>
                    <input type="text" value={form.complemento} onChange={e => set('complemento', e.target.value)} placeholder="Apto, Bloco..." className={inputClass()} />
                  </div>
                  <div className="md:col-span-3">
                    <label className={labelClass}>Bairro</label>
                    <input type="text" value={form.bairro} onChange={e => set('bairro', e.target.value)} placeholder="Bairro" className={inputClass()} />
                  </div>
                  <div className="md:col-span-4">
                    <label className={labelClass}>Cidade *</label>
                    <input type="text" value={form.cidade} onChange={e => set('cidade', e.target.value)} placeholder="Cidade" className={inputClass('cidade')} />
                    {errors.cidade && <p className="text-red-400 text-xs mt-1">{errors.cidade}</p>}
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelClass}>Estado</label>
                    <select value={form.estado} onChange={e => set('estado', e.target.value)} className={inputClass()}>
                      {ESTADOS_BR.map(uf => <option key={uf} value={uf}>{uf}</option>)}
                    </select>
                  </div>
                </div>
              </div>

              {/* Valores */}
              <div className="bg-bg-card border border-border-light rounded-sm p-6">
                <h2 className="text-[11px] font-medium uppercase tracking-[0.18em] text-accent mb-5">
                  Valores
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={labelClass}>Preço *</label>
                    <input
                      type="text"
                      value={form.preco ? formatCurrency(form.preco) : ''}
                      onChange={e => set('preco', parseCurrency(e.target.value))}
                      placeholder="R$ 0,00"
                      className={inputClass('preco')}
                    />
                    {errors.preco && <p className="text-red-400 text-xs mt-1">{errors.preco}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Condomínio / mês</label>
                    <input
                      type="text"
                      value={form.condominio ? formatCurrency(form.condominio) : ''}
                      onChange={e => set('condominio', parseCurrency(e.target.value))}
                      placeholder="R$ 0,00"
                      className={inputClass()}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>IPTU / ano</label>
                    <input
                      type="text"
                      value={form.iptu ? formatCurrency(form.iptu) : ''}
                      onChange={e => set('iptu', parseCurrency(e.target.value))}
                      placeholder="R$ 0,00"
                      className={inputClass()}
                    />
                  </div>
                </div>
              </div>

              {/* Características */}
              <div className="bg-bg-card border border-border-light rounded-sm p-6">
                <h2 className="text-[11px] font-medium uppercase tracking-[0.18em] text-accent mb-5">
                  Características
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {[
                    { field: 'area_total' as const, label: 'Área Total (m²)', required: false },
                    { field: 'area_util' as const, label: 'Área Útil (m²)', required: true },
                    { field: 'quartos' as const, label: 'Quartos', required: false },
                    { field: 'suites' as const, label: 'Suítes', required: false },
                    { field: 'banheiros' as const, label: 'Banheiros', required: false },
                    { field: 'vagas' as const, label: 'Vagas', required: false },
                    { field: 'andar' as const, label: 'Andar', required: false },
                    { field: 'total_andares' as const, label: 'Total Andares', required: false },
                  ].map(({ field, label, required }) => (
                    <div key={field}>
                      <label className={labelClass}>{label}{required ? ' *' : ''}</label>
                      <input
                        type="number"
                        min="0"
                        value={form[field]}
                        onChange={e => set(field, e.target.value)}
                        placeholder="0"
                        className={inputClass(required ? field : undefined)}
                      />
                      {required && errors[field] && <p className="text-red-400 text-xs mt-1">{errors[field]}</p>}
                    </div>
                  ))}
                </div>
              </div>

              {/* Diferenciais */}
              <div className="bg-bg-card border border-border-light rounded-sm p-6">
                <h2 className="text-[11px] font-medium uppercase tracking-[0.18em] text-accent mb-5">
                  Diferenciais & Comodidades
                </h2>
                <div className="flex flex-wrap gap-2">
                  {DIFERENCIAIS_OPTIONS.map(item => (
                    <button
                      key={item}
                      type="button"
                      onClick={() => toggleDiferencial(item)}
                      className={`px-4 py-2 text-xs font-medium uppercase tracking-[0.1em] rounded-sm border transition-all duration-200 ${
                        form.diferenciais.includes(item)
                          ? 'bg-accent text-bg border-accent' :'bg-transparent text-fg-muted border-border-light hover:border-accent/50 hover:text-fg'
                      }`}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>

              {/* Fotos */}
              <div className="bg-bg-card border border-border-light rounded-sm p-6">
                <h2 className="text-[11px] font-medium uppercase tracking-[0.18em] text-accent mb-5">
                  Fotos do Imóvel
                </h2>

                {/* Upload Area */}
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-border-light hover:border-accent/50 rounded-sm p-10 text-center cursor-pointer transition-colors duration-200 group mb-4"
                >
                  <div className="w-12 h-12 mx-auto mb-3 rounded-sm bg-bg flex items-center justify-center group-hover:bg-accent/10 transition-colors">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-fg-muted group-hover:text-accent transition-colors">
                      <path d="M10 13V3M10 3L7 6M10 3l3 3" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M3 14v1a2 2 0 002 2h10a2 2 0 002-2v-1" strokeLinecap="round" />
                    </svg>
                  </div>
                  <p className="text-sm text-fg-muted group-hover:text-fg transition-colors">
                    Clique para selecionar fotos
                  </p>
                  <p className="text-xs text-fg-subtle mt-1">JPG, PNG, WEBP — múltiplos arquivos</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handlePhotoUpload}
                  />
                </div>

                {/* Photo Grid */}
                {photoPreview.length > 0 && (
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                    {photoPreview.map((src, idx) => (
                      <div key={idx} className="relative group aspect-square rounded-sm overflow-hidden bg-bg">
                        <img src={src} alt={`Foto ${idx + 1} do imóvel`} className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-bg/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => removePhoto(idx)}
                            className="w-8 h-8 rounded-full bg-red-500/80 flex items-center justify-center"
                          >
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="1.5">
                              <path d="M2 2l8 8M10 2l-8 8" strokeLinecap="round" />
                            </svg>
                          </button>
                        </div>
                        {idx === 0 && (
                          <span className="absolute top-1.5 left-1.5 bg-accent text-bg text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm">
                            Capa
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="flex items-center justify-between pt-2">
                <p className="text-xs text-fg-subtle">* Campos obrigatórios</p>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-10 py-3.5 bg-accent text-bg font-semibold text-[11px] uppercase tracking-widest rounded-sm hover:bg-accent-light transition-colors duration-200 btn-cta-pulse disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {saving && (
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                  )}
                  {saving ? 'Salvando...' : 'Cadastrar Imóvel'}
                </button>
              </div>

            </div>
          </form>
        )}

        {/* LISTING TAB */}
        {activeTab === 'listagem' && (
          <div>
            {loading ? (
              <div className="bg-bg-card border border-border-light rounded-sm p-16 text-center">
                <div className="w-8 h-8 mx-auto mb-4 border-2 border-accent border-t-transparent rounded-full animate-spin" />
                <p className="text-sm text-fg-muted">Carregando portfólio...</p>
              </div>
            ) : fetchError ? (
              <div className="bg-bg-card border border-red-500/30 rounded-sm p-16 text-center">
                <p className="text-sm text-red-400 mb-4">{fetchError}</p>
                <button
                  onClick={loadProperties}
                  className="px-6 py-2.5 bg-accent text-bg font-semibold text-[11px] uppercase tracking-widest rounded-sm hover:bg-accent-light transition-colors"
                >
                  Tentar novamente
                </button>
              </div>
            ) : properties.length === 0 ? (
              <div className="bg-bg-card border border-border-light rounded-sm p-16 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-sm bg-bg flex items-center justify-center">
                  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.2" className="text-fg-subtle">
                    <path d="M4 24V10l10-7 10 7v14H4z" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M10 24v-7h8v7" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p className="font-display text-xl italic text-fg-muted mb-2">Nenhum imóvel cadastrado</p>
                <p className="text-sm text-fg-subtle mb-6">Adicione o primeiro imóvel ao portfólio</p>
                {!authLoading && user && (
                <button
                  onClick={() => setActiveTab('cadastrar')}
                  className="px-8 py-3 bg-accent text-bg font-semibold text-[11px] uppercase tracking-widest rounded-sm hover:bg-accent-light transition-colors"
                >
                  Cadastrar Imóvel
                </button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {properties.map(prop => (
                  <div key={prop.id} className="bg-bg-card border border-border-light rounded-sm overflow-hidden bento-card group">
                    {/* Photo */}
                    <div className="aspect-video bg-bg relative overflow-hidden">
                      {prop.fotos?.length > 0 ? (
                        <img src={prop.fotos[0]} alt={`Foto principal de ${prop.titulo}`} className="w-full h-full object-cover img-zoom" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="1" className="text-fg-subtle">
                            <rect x="2" y="6" width="28" height="20" rx="2" />
                            <circle cx="11" cy="13" r="3" />
                            <path d="M2 24l7-7 5 5 4-4 8 8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                      )}
                      <div className="absolute top-3 left-3 flex gap-2">
                        <span className="bg-accent text-bg text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm">
                          {prop.finalidade}
                        </span>
                        <span className={`text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-sm ${
                          prop.status === 'Disponível' ? 'bg-green-500/80 text-white' :
                          prop.status === 'Em Negociação'? 'bg-yellow-500/80 text-bg' : 'bg-fg-subtle text-bg'
                        }`}>
                          {prop.status}
                        </span>
                      </div>
                      {prop.fotos?.length > 1 && (
                        <span className="absolute bottom-3 right-3 bg-bg/80 text-fg text-[10px] px-2 py-1 rounded-sm">
                          +{prop.fotos.length - 1} fotos
                        </span>
                      )}
                    </div>

                    {/* Info */}
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div>
                          <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-accent mb-1">{prop.tipo}</p>
                          <h3 className="font-display text-lg italic text-fg leading-tight">{prop.titulo}</h3>
                        </div>
                        {prop.preco && (
                          <p className="text-accent font-semibold text-base whitespace-nowrap">
                            {formatCurrency(prop.preco)}
                          </p>
                        )}
                      </div>

                      {(prop.logradouro || prop.cidade) && (
                        <p className="text-xs text-fg-muted mb-3 flex items-center gap-1.5">
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" className="flex-shrink-0">
                            <path d="M6 1a3.5 3.5 0 010 7C3.5 8 1 5.5 1 3.5A3.5 3.5 0 016 0z" />
                            <circle cx="6" cy="3.5" r="1" />
                            <path d="M6 8v3" strokeLinecap="round" />
                          </svg>
                          {[prop.bairro, prop.cidade, prop.estado].filter(Boolean).join(', ')}
                        </p>
                      )}

                      {/* Stats */}
                      <div className="flex items-center gap-4 text-xs text-fg-muted border-t border-border-light pt-3">
                        {prop.area_util && (
                          <span className="flex items-center gap-1">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <rect x="1" y="1" width="10" height="10" rx="1" />
                            </svg>
                            {prop.area_util}m²
                          </span>
                        )}
                        {prop.quartos && (
                          <span className="flex items-center gap-1">
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
                              <path d="M1 8V4a1 1 0 011-1h8a1 1 0 011 1v4" strokeLinecap="round" />
                              <path d="M1 8h10M1 10h10" strokeLinecap="round" />
                            </svg>
                            {prop.quartos} qts
                          </span>
                        )}
                        {prop.banheiros && (
                          <span>{prop.banheiros} ban</span>
                        )}
                        {prop.vagas && (
                          <span>{prop.vagas} vaga{parseInt(prop.vagas) > 1 ? 's' : ''}</span>
                        )}
                        <span className="ml-auto text-fg-subtle">{formatDate(prop.created_at)}</span>
                      </div>

                      {/* Diferenciais */}
                      {prop.diferenciais?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {prop.diferenciais.slice(0, 4).map(d => (
                            <span key={d} className="text-[9px] uppercase tracking-wider text-fg-subtle border border-border-light px-2 py-0.5 rounded-sm">
                              {d}
                            </span>
                          ))}
                          {prop.diferenciais.length > 4 && (
                            <span className="text-[9px] uppercase tracking-wider text-accent px-2 py-0.5">
                              +{prop.diferenciais.length - 4}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </main>
  );
}
